import { useMemo, useState } from "react";
import { Clock, BookOpen, CheckCircle2, AlertCircle, RotateCcw, Trophy, Sparkles, Activity } from "lucide-react";
import type { ParentAnalyticsResult, ActivityEvent } from "@/hooks/useParentAnalytics";
import { parentReadableSubtype } from "@/lib/parent/activityLabels";
import { ACCENT_CLS, type AccentKey } from "@/components/parent/shared/parentPrimitives";
import { cn } from "@/lib/utils";

interface Props {
  analytics: ParentAnalyticsResult;
  childName: string;
  limit?: number;
}

/**
 * Phase 3 — Recent Activity Timeline.
 *
 * Renders the most recent learning events with parent-readable labels
 * (never raw enums) and a "why it matters" line derived from event type
 * and score. Quiet, premium, scan-friendly.
 */
export default function RecentActivityTimeline({ analytics, childName, limit = 8 }: Props) {
  const [expanded, setExpanded] = useState(false);
  const first = childName.split(" ")[0] || childName;

  const events = useMemo(() => {
    return [...analytics.recentActivity]
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
      .slice(0, expanded ? limit * 2 : limit);
  }, [analytics.recentActivity, limit, expanded]);

  if (events.length === 0) {
    const hasBetaProgress = analytics.betaPathCompleted > 0 || analytics.lessonCount > 0;
    return (
      <section className="relative overflow-hidden rounded-2xl border border-white/[0.07] ring-1 ring-inset ring-white/[0.03] bg-[linear-gradient(180deg,hsl(var(--card)/0.7)_0%,hsl(var(--card)/0.45)_100%)] p-5 backdrop-blur-2xl">
        <Header />
        {hasBetaProgress ? (
          <p className="mt-4 text-[12px] leading-relaxed text-muted-foreground">
            {first} has logged {analytics.betaPathCompleted}/{analytics.betaPathTotal} learning path
            moments, but no detailed events have been recorded yet. Individual lessons and quizzes
            will appear here as they happen.
          </p>
        ) : (
          <p className="mt-4 text-[12px] text-muted-foreground">
            No activity recorded yet. {first}'s first lesson will appear here.
          </p>
        )}
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden rounded-2xl border border-white/[0.07] ring-1 ring-inset ring-white/[0.03] bg-[linear-gradient(180deg,hsl(var(--card)/0.7)_0%,hsl(var(--card)/0.45)_100%)] p-5 backdrop-blur-2xl shadow-[0_18px_46px_-30px_rgba(0,0,0,0.65),inset_0_1px_0_0_hsl(var(--foreground)/0.05)] sm:p-6">
      <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent" />
      <div className="relative">
        <Header />
        <ol className="mt-4 space-y-3">
          {events.map((ev, i) => {
            const meta = describeEvent(ev);
            const cls = ACCENT_CLS[meta.accent];
            return (
              <li key={ev.id} className="relative flex gap-3">
                <div className="relative flex flex-col items-center">
                  <span className={cn("flex h-8 w-8 items-center justify-center rounded-xl ring-1", cls.chip)}>
                    <meta.icon className="h-3.5 w-3.5" />
                  </span>
                  {i < events.length - 1 && (
                    <span className="mt-1 w-px flex-1 bg-gradient-to-b from-white/10 via-white/[0.04] to-transparent" />
                  )}
                </div>
                <div className="min-w-0 flex-1 pb-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
                    <p className="min-w-0 truncate text-[13px] font-medium text-foreground">{ev.title}</p>
                    <p className="shrink-0 text-[10.5px] text-muted-foreground/80">{formatWhen(ev.at)}</p>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1", cls.chip)}>
                      <span className={cn("h-1 w-1 rounded-full", cls.dot)} />
                      {parentReadableSubtype(ev.subtype)}
                    </span>
                    {ev.topic && (
                      <span className="inline-flex items-center rounded-full bg-white/[0.04] px-2 py-0.5 text-[10px] text-muted-foreground ring-1 ring-white/[0.06]">
                        {ev.topic}
                      </span>
                    )}
                    {ev.score != null && (
                      <span className="inline-flex items-center rounded-full bg-white/[0.04] px-2 py-0.5 text-[10px] text-foreground/85 ring-1 ring-white/[0.06]">
                        Score {Math.round(ev.score)}%
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 text-[11.5px] leading-snug text-muted-foreground/85">{meta.why}</p>
                </div>
              </li>
            );
          })}
        </ol>
        {analytics.recentActivity.length > limit && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-2 w-full rounded-xl border border-white/[0.06] bg-background/30 px-3 py-2 text-[11px] font-medium text-muted-foreground transition hover:border-white/[0.1] hover:text-foreground"
          >
            {expanded ? "Show fewer" : `Show more (${Math.min(limit, analytics.recentActivity.length - limit)} more)`}
          </button>
        )}
      </div>
    </section>
  );
}

function Header() {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-300 ring-1 ring-cyan-400/25">
        <Activity className="h-3.5 w-3.5" />
      </span>
      <div>
        <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground/85">Recent Activity</p>
        <h3 className="font-heading text-base font-semibold tracking-tight text-foreground sm:text-[17px]">Latest learning moments</h3>
      </div>
    </div>
  );
}

function describeEvent(ev: ActivityEvent): { icon: React.ComponentType<{ className?: string }>; accent: AccentKey; why: string } {
  switch (ev.subtype) {
    case "LESSON_COMPLETED":
      return { icon: BookOpen, accent: "cyan", why: "Adds to learning path progress and unlocks the next concept." };
    case "QUIZ_PASSED":
      return { icon: CheckCircle2, accent: "emerald", why: ev.score != null && ev.score >= 90 ? "Strong mastery signal — concept is well anchored." : "Confirms the concept is understood enough to move on." };
    case "QUIZ_FAILED":
      return { icon: AlertCircle, accent: "amber", why: "A short review tonight closes the gap before it sticks." };
    case "QUIZ_RETAKEN":
      return { icon: RotateCcw, accent: "amber", why: "Retake recorded — worth a quick walk-through together." };
    case "COURSE_STARTED":
      return { icon: Sparkles, accent: "cyan", why: "New direction opened. First few lessons set the cadence." };
    case "COURSE_COMPLETED":
      return { icon: Trophy, accent: "emerald", why: "Full module completed — meaningful milestone worth naming out loud." };
    case "ACHIEVEMENT_EARNED":
      return { icon: Trophy, accent: "violet", why: "A milestone was earned through consistent learning." };
    default:
      return { icon: Clock, accent: "neutral", why: "Recorded learning activity." };
  }
}

function formatWhen(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diffMs = Date.now() - t;
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}